import * as React from "react";
import { useEffect, useState, useMemo } from "react";
import { useAppDispatch, useDataGridCustomLocaleText } from "../../../app/hooks";
import { unwrapResult } from "@reduxjs/toolkit";
import { Box, Paper, TableContainer, Stack, IconButton, Tooltip, Button, ButtonGroup } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import { format } from "date-fns";

import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { ProjectProcessHeader } from "../../project/projectProcess/ProjectProcessApiClient";
import { ValidationProcessScenarioChip } from "../../project/validationProcess/components/ValidationProcessScenarioChip";
import { loadValidationProcessForDevice, loadValidationProcessForSystem } from "../../project/validationProcess/validationProcessApi";
import { ValidationProcessStatusPill } from "../../project/validationProcess/components/ValidationProcessStatusPill";
import { Localized, useLocalization } from "@fluent/react";
import { ProcessNumberBox } from "../../../app/components/ProcessNumberBox";
import { StructureName } from "../../device/Structure";
import { AppId } from "../../../app/appTypes";
import { useEntityNavigation } from "../../../app/components/EntityBreadcrumbs";

export const DeviceValidationsPanel = ({
    deviceId,
    onCreateClick,
}: {
    deviceId: AppId;
    onCreateClick?: (deviceId: AppId) => void;
}) => {
    const { l10n } = useLocalization()

    const dispatch = useAppDispatch();
    const navigateToEntity = useEntityNavigation({ type: "device", id: deviceId });
    const localeText = useDataGridCustomLocaleText();

    const [rows, setRows] = useState<ProjectProcessHeader[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<any>(null);

    const isActiveStatus = (s?: string | null) => {
        const v = (s ?? "").toString().toUpperCase();
        return v && v !== "COMPLETED" && v !== "CANCELLED" && v !== "ARCHIVED";
    };

    const activeProcess = rows.find(r => isActiveStatus(r.status));
    const hasActiveProcess = !!activeProcess;

    const loadData = React.useCallback(() => {
        setLoading(true);
        setError(null);
        dispatch(loadValidationProcessForDevice(deviceId))
            .then(unwrapResult)
            .then((list) => setRows(list ?? []))
            .catch((e) => setError(e))
            .finally(() => setLoading(false));
    }, [dispatch, deviceId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleOpenProcess = (id: number | string) => {
        navigateToEntity({ type: "process", id: String(id) })
    };

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'no',
                headerName: l10n.getString('project-no', null, 'Numer Procesu'),
                minWidth: 200,
                width: 200,
                maxWidth: 200,
                renderCell: (params) => (
                    <ProcessNumberBox value={params.value} />
                ),

            },
            {
                field: 'name',
                headerName: l10n.getString('project-process', null, 'Proces'),
                flex: 1
            },
            {
                field: 'structureId',
                headerName: l10n.getString("structure", null, "_structure"),
                flex: 1,
                minWidth: 200,

                renderCell: (params) => {
                    return (
                        <StructureName structureId={params.row.structureId} />
                    )
                }
            },
            {
                field: 'scenario',
                headerName: l10n.getString('project-scenario', null, 'Scenariusz'),
                width: 160,
                minWidth: 160,
                maxWidth: 160,
                sortable: false,
                resizable: false,
                renderCell: (params) => (
                    <ValidationProcessScenarioChip scenario={params.value} />
                ),
            },
            {
                field: 'createDate',
                width: 150,
                headerName: l10n.getString('process-create-date', null, 'Data utworzenia'),
                valueGetter: (_, row) => {
                    return row.createDate ? new Date(row.createDate) : "-";
                },
                valueFormatter: (value) => {
                    if (!value) {
                        return "";
                    }
                    return format(value, 'dd-MM-yyyy');
                }
            },
            {
                field: 'startDate',
                headerName: l10n.getString("process-date-from", null, "_Data rozpoczęcia"),
                align: 'center',
                headerAlign: 'center',
                minWidth: 200,
                width: 200,
                maxWidth: 200,
                valueGetter: (_, row) => {
                    return row.startDate ? new Date(row.startDate).toISOString().split('T')[0] : "-";
                }
            },
            {
                field: 'finishDate',
                headerName: l10n.getString("process-date-to", null, "_Data zakończenia"),
                align: 'center',
                headerAlign: 'center',
                minWidth: 200,
                width: 200,
                maxWidth: 200,
                valueGetter: (_, row) => {
                    return row.finishDate ? new Date(row.finishDate).toISOString().split('T')[0] : "-";
                }
            },

            {
                field: 'status',
                headerName: l10n.getString('project-status', null, 'Status'),
                width: 160,
                minWidth: 160,
                maxWidth: 160,
                headerAlign: 'center',
                align: 'center',
                sortable: false,
                resizable: false,
                renderCell: (params) => (
                    <div>
                        <ValidationProcessStatusPill status={params.row.status ?? ""} />
                    </div>
                )
            },
            {
                field: 'actions', type: 'actions', width: 200,
                renderCell: (params) => {
                    return (
                        <ButtonGroup>
                            <Button onClick={() => handleOpenProcess(params.id)} color="secondary" variant="outlined">
                                <Localized id="go-to">
                                    <span>Edytuj</span>
                                </Localized>
                            </Button>
                        </ButtonGroup>
                    )
                }
            }
        ],
        [l10n, navigateToEntity]
    );

    if (loading) return <LoadingContainer />;
    if (error) return <ErrorContainer error={error} />;

    return (
        <TableContainer component={Paper}>
            <Box sx={{ display: "flex", height: "100%" }}>
                <Box sx={{ flexGrow: 1 }}>
                    <DataGrid
                        style={{ minHeight: 100, height: "calc(100vh - 360px)" }}
                        localeText={localeText}
                        rows={rows}
                        columns={columns}
                        disableRowSelectionOnClick
                        slots={{
                            toolbar: () => (
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1 }}>
                                    <QuickSearchToolbar />
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Tooltip title={l10n.getString("refresh", null, "Odśwież")}>
                                            <IconButton size="small" onClick={loadData}>
                                                <RefreshIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            ),
                        }}
                        initialState={{
                            sorting: { sortModel: [{ field: "startDate", sort: "desc" }] },
                        }}
                    />
                </Box>
            </Box>
        </TableContainer>
    );
};
